import { SelectOption } from '@globaleris/ui.library'
import {
  EnumCurrency,
  EnumEstadoCliente,
  EnumEstadoProductoCapacidad,
  EnumServicio,
  EnumTipoPeaje,
  EnumTipoProductoCapacidad
} from '../../api'
import {
  AnnexedContractModel,
  ContractOrdersRequestModel,
  ContractQuantity
} from '../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumCommodity } from '../../api/Argentia/enums'
import {
  AccountModel,
  ActivityTypeModel,
  CapacityModel,
  ContractATRModel,
  ContractAddressForm,
  ContractModel,
  DistributorModel,
  NewCUPSModel,
  NewProductModel,
  PaymentMethodModel,
  PowerModel,
  ProductModel,
  ProductModelPartial
} from '../../api/Argentia/model/contracts'
import { EnumHold } from '../../api/oData/enums/enumHold'
import { EnumPresionTension } from '../../api/oData/enums/enumPresionTension'
import { EnumTipoMetodoPago } from '../../api/oData/enums/enumTipoMetodoPago'
import { SearchSelectOption } from '../../model/searchSelectOption'
import { QuotesModel } from '../../api/Argentia/endpoints/quotes/model/quotes'

export interface ContractDataFormModel {
  contractType?: number
  contractCode?: string
  products?: number[]
  productsList?: ProductModelPartial[]
  distributorModel?: DistributorModel
  startDate?: Date
  endDate?: Date
  signDate?: Date
  cups?: string
  telemetered?: boolean
  toll?: EnumTipoPeaje
  pressureGroup?: EnumPresionTension
  distributor?: number
  qaEstimated?: number
  qdHired?: number
  period1?: number
  period2?: number
  period3?: number
  period4?: number
  period5?: number
  period6?: number
  billingCycle?: number
  paymentMethod?: EnumTipoMetodoPago
  iban?: string
  exists?: boolean
  periodsNum?: number
  service?: EnumServicio
  contractCapacityType?: EnumTipoProductoCapacidad
  cnae?: number
  cnaeModel?: ActivityTypeModel
  aTRCode?: string
  aTRType?: number
  rawMaterial?: boolean
  book?: number
  description?: string
  hold?: EnumHold
  subjectID?: number
  postalAddress?: string
  postalCodeSearch?: SelectOption
  postalCode?: string
  municipality?: string
  province?: string
  country?: string
  municipalityId?: number
  provinceId?: number
  countryId?: number

  orderNm?: string
}

export interface DBContractSuppliesProps {
  cups: CupsFormModel | null
  contractATR: ContractATRFormModel | null
  capacities: CapacityFormModel[] | null
}

// This model is meant to be used for the form component, which has a SEARCHTYPE input.
export type ContractFormRaw = ContractMainForm &
  ContractAccountForm &
  AddressFormRaw &
  ProductTable &
  AditionalsFormModel & {
    useExistingAccount: boolean
  } & AnnexedContractsFormTableModel

export type ContractForm = ContractMainForm &
  ContractAccountForm &
  AddressFormModel &
  ProductTable &
  AditionalsFormModel

export interface ContractMainForm {
  id: number
  contractCode: string
  type: any
  startDate: Date
  endDate: Date | null
  signatureDate: Date
  expectedStartDate: Date | null
  expectedEndDate: Date | null
  state: any
  hold: any
  commodity?: EnumCommodity
  productsList?: ProductModel[]
}

export interface ContractAccountForm {
  accountId: number
  iban: string
  accountCode: string
  billingCycleId: number
  paymentMethodType: number
}

export interface AddressFormModel extends Omit<AddressModel, 'Id'> {
  fiscalAddressId: number
}

export interface AddressFormRaw extends Omit<AddressModel, 'Id'> {
  fiscalAddressId: number
  postalCodeSearch: SearchSelectOption
}

export interface AditionalsFormModel {
  bookID: number
  description: string
  hold: EnumHold
}

export interface AnnexedContractsFormTableModel {
  annexedContracts: AnnexedContractModel[]
}

export type InitialAddressModel = Pick<
  AddressModel,
  | 'country'
  | 'municipality'
  | 'province'
  | 'postalCode'
  | 'countryId'
  | 'municipalityId'
  | 'provinceId'
  | 'postalAddress'
> & { postalCodeSearch: SearchSelectOption }
export interface AddressModel {
  Id: number
  postalAddress: string
  postalCode: string
  municipality?: string
  province?: string
  country?: string
  municipalityId: number
  provinceId: number
  countryId: number
  cupsId?: number
  paymentMethodId: number
}

export interface ProductTable {
  products: NewContractProduct[]
}

export interface ContractProduct {
  id: number
  productId: number
  contractId: number
  code: string
  description: string
  // should be required
  startDate: Date
  endDate?: Date | null
  currency?: EnumCurrency
}

export interface NewContractProduct extends Omit<ContractProduct, 'id' | 'code' | 'description'> {
  id?: number
  tempId?: string
  productCode?: any
  productDescription?: string
  actions?: any // table icon
}

export interface ContractGeneralDataFormModel {
  contractCode: string
  // products?: number[]
  // productsList?: ProductModel[]
  startDate: string
  endDate?: string
  signDate?: string
  state: number
}

export interface CupsFormModel extends Omit<
  Partial<AddressModel>,
  'Id' | 'paymentMethodId' | 'cupsId'
> {
  id: number
  code: string
  distributorId?: number
  telemetered?: boolean
  pressureGroup?: EnumPresionTension
  distributor?: number
  cnaeId?: number
  cnaeCode?: string
  cnaeModel?: ActivityTypeModel
  addressId?: number
  commodity?: number
  logisticElementId?: number
  height?: number
}

export interface CapacityFormModel extends Omit<CapacityModel, 'type' | 'contractType' | 'toll'> {
  type: number
  contractType: number
  toll: number
  signDate?: string
  cupsId?: number
  logisticElementId?: number
  balanceZoneId?: number
  companyId?: number
  nmHours?: number

  tempId?: string
  timeStamp?: number
  canDelete?: boolean
  delete?: boolean
  canEdit?: boolean
  canRequestCapacityMod?: boolean
  canTerminateCapacity?: boolean
  modRequest?: EnumContractCapacitiesOptions
}

export interface ContractCapacitiesFormModel extends Omit<
  CapacityFormModel,
  'type' | 'contractType' | 'toll' | 'code' | 'status' | 'qaEstimada' | 'endDate'
> {
  code?: string
  status?: number
  qaEstimada?: number
  type?: number
  contractType?: number
  toll?: number
  newCode?: string
  newToll?: number
  newQdContratada?: number
  newStartDate?: Date | string
  newEndDate?: Date | string
  endDate?: Date | string
  hasCapBetweenDates?: boolean
}

export interface CapacityInitialValues {
  initialCode?: string
  initialQaEstimada?: number
  initialToll?: number
  // type?: number
  // contractType?: number
  // qdContratada?: number
}

export interface Period extends Omit<PowerModel, 'period'> {
  period: number
}

export interface ContractATRFormModel extends Omit<
  ContractATRModel,
  'versions' | 'currentVersion' | 'type' | 'code' | 'companyID'
> {
  version: number
  idDetail: number
  tollType: number | null
  startDate: Date
  newStartDate?: Date
  newEndDate?: Date
  endDate?: Date
  periods?: Period[]
  period1?: number
  period2?: number
  period3?: number
  period4?: number
  period5?: number
  period6?: number
  period1id?: number
  period2id?: number
  period3id?: number
  period4id?: number
  period5id?: number
  period6id?: number
  generateNewVersion?: boolean
  type?: number // EnumTipoContratoATR
  code?: string
  companyID?: number
  canEdit?: boolean
}

export interface ContractATRAddVersionFormModel extends ContractATRFormModel {
  latestVersionIdDetail: number
  latestVersionNumber: number
}

export interface NewContractModel extends Omit<
  ContractModel,
  | 'id'
  | 'account'
  | 'paymentMethod'
  | 'cupsList'
  | 'productsList'
  | 'hasFlexibilities'
  | 'annexedContracts'
  | 'contractOrders'
> {
  paymentMethod: NewPaymentMethodModel
  cupsList: NewCUPSModel[]
  productsList: NewProductModel[] | undefined
  quotesList?: any[]
  contractOrders?: ContractOrdersRequestModel[]
}

export interface PatchContractBatchRequest {
  contract?: PatchContractModel
  account?: PatchAccountModel
  address?: PatchAddressModel
  paymentMethod?: PatchPaymentMethodModel
  products?: PatchContractProductModel[]
  deletedProducts?: DeleteContractProductModel[]
}

export interface PatchContractSuppliesBatchRequest {
  cups?: PatchCUPSModel
  address?: PatchAddressModel
  contractATR?: PatchContractATR
  contractATRDetails?: PatchContractATRDetail[]
  powers?: PatchPower[]
  capacities?: PatchCapacity[]
}

export type SuppliesForm = SuppliesFormData & (SuppliesGasFormData & SuppliesPowerFormData)

export interface SuppliesFormData {
  cups: CupsFormModel
}

export interface SuppliesGasFormData {
  // contractATR?: never
  capacities: CapacityFormModel[] | null
}

export interface SuppliesPowerFormData {
  contractATR: ContractATRFormModel | null
  // capacities?: never
}

export interface PatchCUPSModel {
  id: number
  code?: string
  telemetered?: boolean
  cnae?: string // code
  pressureGroupId?: number
  height?: number
}

export interface PatchContractATR {
  id?: number
  type?: number
  code?: string
  state?: number
  cupsId?: number // only for Post
  companyId?: number // only for Post
}

export interface PatchContractATRDetail {
  id?: number
  idContractATR?: number
  startDate?: string
  endDate?: string
  tollType?: number | null
  delete?: boolean
}

export interface PatchPower {
  idContractATRDetail?: number
  idPower?: number
  value?: number
  period?: number
  delete?: boolean
  tempIdPower?: string
}

export interface PatchCapacity {
  id?: number
  tempId?: number
  code?: string
  type?: number
  contractType?: number
  qaEstimada?: number
  qdContratada?: number
  toll?: number
  startDate?: string
  endDate?: string
  signDate?: string
  rawMaterial?: boolean
  balanceZone?: number
  cupsId?: number
  companyId?: number
  delete?: boolean
  nmHours?: number
  status?: number
  logisticElementId?: number
}

export interface PatchContractModel extends Pick<
  ContractModel,
  'id' | 'contractCode' | 'description' | 'signatureDate' | 'status' | 'bookID' | 'hold'
> {
  status?: number
  startDate?: string
  endDate?: string | null
  type?: number
}

export interface PatchAccountModel extends Pick<
  AccountModel,
  'id' | 'code' | 'description' | 'billingCycleId'
> {}

export interface PatchAddressModel extends Partial<
  Pick<
    ContractAddressForm,
    'postalAddress' | 'postalCode' | 'countryId' | 'provinceId' | 'municipalityId'
  >
> {
  id: number
}

export interface PatchPaymentMethodModel extends Pick<PaymentMethodModel, 'iban'> {
  id: number
  type: string
}

export interface PatchContractProductModel extends Omit<
  NewContractProduct,
  'id' | 'tempId' | 'startDate' | 'endDate'
> {
  contractProductId?: number
  cupsId?: number
  startDate?: string
  endDate?: string
}

export interface DeleteContractProductModel {
  contractProductId: number
}

export type NewAddressModel = Omit<
  AddressModel,
  'Id' | 'paymentMethodId' | 'municipalityId' | 'provinceId' | 'countryId'
> & {
  municipalityId: number
  provinceId: number
  countryId: number
}

export interface NewPaymentMethodModel extends Omit<PaymentMethodModel, 'Id' | 'address' | 'iban'> {
  iban?: string
  address?: NewAddressModel
}

export interface CapacityEditFormDialogValidations {
  isDirty: boolean
  startDate: boolean
  endDate: boolean
  startDateContract: boolean
  endDateContract: boolean
}

export interface ProductEditFormDialogValidations {
  isDirty: boolean
  startDate: boolean
  endDate: boolean
}

export interface ContractEditFormValidations extends ContractsFormValidations {
  postalCode: boolean
}

export interface ContractSuppliesFormValidations extends ContractsFormValidations {
  postalCode: boolean
  cupsCode: boolean
  newStartDate: boolean
  newEndDate: boolean
  startDateContract: boolean
  endDateContract: boolean
  newStartDateContract: boolean
  newEndDateContract: boolean
  newStartDatePast: boolean
}

export interface ContractsFormValidations {
  isDirty: boolean
  startDate: boolean
  endDate: boolean
  signDate: boolean
  contractCode: boolean
  iban: boolean
}

export namespace RulebookContractSuppliesValidations {
  export const CUPS_CODE = 'cupsCode'
  export const POSTAL_CODE = 'postalCode'
  export const NEW_START_DATE = 'newStartDate'
  export const NEW_END_DATE = 'newEndDate'
  export const START_DATE = 'startDate'
  export const END_DATE = 'endDate'
  export const START_DATE_CONTRACT = 'startDateContract'
  export const END_DATE_CONTRACT = 'endDateContract'
  export const NEW_START_DATE_CONTRACT = 'newStartDateContract' // ContractATR new version
  export const NEW_END_DATE_CONTRACT = 'newEndDateContract' // ContractATR new version
  export const NEW_START_DATE_PAST = 'newStartDatePast' // ContractATR new version
  export const NEW_START_DATE_FUTURE = 'newStartDateFuture' // ContractATR new version
}

export type RulebookContractSuppliesValidations =
  (typeof RulebookContractSuppliesValidations)[keyof typeof RulebookContractSuppliesValidations]

export namespace RulebookContractEditValidations {
  export const POSTAL_CODE = 'postalCode'
}

export type RulebookContractEditValidations =
  (typeof RulebookContractEditValidations)[keyof typeof RulebookContractEditValidations]

export namespace RulebookContractsValidations {
  export const START_DATE = 'startDate'
  export const END_DATE = 'endDate'
  export const SIGN_DATE = 'signDate'
  export const CONTRACT_CODE = 'contractCode'
  export const IBAN = 'iban'
}

export type RulebookContractsValidations =
  (typeof RulebookContractsValidations)[keyof typeof RulebookContractsValidations]

export interface ChangeContractOwnershipModel {
  clientId: number
  codContractOld: string
  contractNew: Omit<NewContractModel, 'contractCode'>
  atr: ChangeOwnerShipATRFormModel | null
  cnae?: string
  oldContractState?: EnumEstadoCliente
}

export interface RevertChangeOwnershipModel {
  newContractId: number
  oldContractId: number
}

export interface ChangeOwnerShipATRFormModel {
  tollType?: number
  tipoProductoCapacidad?: number
  contractType?: number
  qdContratada?: number
  qaEstimada?: number
  state?: EnumEstadoProductoCapacidad
  contractAtrType?: number
  powers?: PatchPower[]
}

export interface TerminateContractOwnershipModel {
  contractId: number
  terminationDate: string
}

export type ContractQuantityFormModel = Omit<ContractQuantity, 'id'> & { id?: number }

export enum EnumContractCapacitiesOptions {
  Toll = 11,
  Capacity = 13,
  Both = 15
}
